import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

interface ToastItem {
  id: number;
  message: string;
  kind: 'info' | 'error';
}

interface ToastApi {
  show: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => {}, error: () => {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((message: string, kind: ToastItem['kind']) => {
    const id = nextId.current++;
    setItems((list) => [...list.slice(-3), { id, message, kind }]);
    setTimeout(() => setItems((list) => list.filter((item) => item.id !== id)), kind === 'error' ? 7000 : 3500);
  }, []);

  const api = useMemo<ToastApi>(() => ({ show: (message) => push(message, 'info'), error: (message) => push(message, 'error') }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`toast ${item.kind === 'error' ? 'is-error' : ''}`} role={item.kind === 'error' ? 'alert' : 'status'}>
            <Icon name={item.kind === 'error' ? 'alert' : 'check'} />
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook lives with its provider
export const useToast = () => useContext(ToastContext);
